import { computed, ComputedRef } from '@nuxtjs/composition-api';
import { GlobalConfig } from '@shared/types/GlobalConfig';
import { ProductViewModel } from '@shared/types/ProductViewModel';
import { Product, productGetters } from '@unified-commerce/gpc-vue-storefront-shopify';
import { isValid, parseISO } from 'date-fns';

import { BRAND, CATEGORY_ROUTE_PREFIX, SEARCH_CATEGORY_TENANT_PREFIX_HANDLE } from '~/constants';

const parsePromoEndDate = (endDate: string, productTitle: string): Date | undefined => {
  const parsedEndDate = parseISO(endDate);
  if (isValid(parsedEndDate)) {
    return parsedEndDate;
  }
  console.warn(`Product ${productTitle} promotion end date ${endDate} is not a valid date format.`);
};

const mapGallery = (gallery: any[], alt: string) =>
  gallery.map((media) => ({
    mobile: { url: media.small },
    desktop: { url: media.normal },
    big: { url: media.big },
    alt,
    video: media.video ?? null,
  }));

const getGalleryWithPlaceholder = (product: any) => {
  const placeholderImage = {
    originalSrc:
      'https://cdn.shopify.com/s/files/1/0407/1902/4288/files/placeholder_600x600.jpg?v=1625742127',
  };

  const productWithImages = {
    ...product,
    // Guard against product.images being undefined (optional chain on both)
    images: product?.images?.length === 0 ? [placeholderImage] : product?.images ?? null,
  };

  const gallery = mapGallery(productGetters.getGallery(productWithImages), product?.name || '');

  // getGallery returns [] when media.edges has items but all lack previewImage; fall back to placeholder
  if (gallery.length === 0) {
    return mapGallery(productGetters.getGallery(null as unknown as Product), '');
  }

  return gallery;
};

// eslint-disable-next-line max-lines-per-function
export const toProductViewModel = (
  product: ComputedRef<Product>,
  config: Required<GlobalConfig>,
  featureFlagEnabled?: boolean,
): ProductViewModel => {
  const title = computed(() => productGetters.getName(product.value));
  const variantImageIndex = computed(() =>
    productGetters.getVariantImageIndex(
      product.value,
      productGetters.hasVariantSelected(product.value)
        ? productGetters.getVariantBySelectedOptions(product.value)?.image?.originalSrc
        : '',
    ),
  );

  return {
    variantImageIndex,
    title,
    promotionEndDate: computed(() =>
      parsePromoEndDate(productGetters.getPromotionEndDate(product.value), title.value),
    ),
    id: computed(() => productGetters.getId(product.value)),
    description: computed(() => productGetters.getDescription(product.value)),
    descriptionHtml: computed(() => productGetters.getDescription(product.value, true)),
    partNumber: computed(() =>
      productGetters.getProductVariantMetafield(product.value, 'partNumber'),
    ),
    price: computed(() => productGetters.getPrice(product.value) ?? 0),
    compareToPrice: computed(() => productGetters.getCompareToPrice(product.value) ?? null),
    handle: computed(() => productGetters.getSlug(product.value)),
    priceRange: computed(() => productGetters.getPriceRange(product.value) ?? null),
    sku: computed(() => productGetters.getSKU(product.value)),
    asku: computed(() => productGetters.getProductVariantMetafield(product.value, 'asku')),
    stock: computed(() => productGetters.getStock(product.value)),
    stockQty: computed(() => {
      if (featureFlagEnabled) {
        const isDigital = !productGetters.getRequiresShipping(product.value);

        if (isDigital) {
          return productGetters.isInStock(product.value) ? config.digitalProduct.maxQuantity : 0;
        }

        return productGetters.getStock(product.value, { supportFreeSale: config.supportFreeSale });
      }

      return productGetters.getStock(product.value, { supportFreeSale: config.supportFreeSale });
    }),
    featuredImage: computed(() => productGetters.getFeaturedImage(product.value)),
    onlineStoreUrl: computed(() => productGetters.getOnlineStoreUrl(product.value)),
    vendor: computed(() => productGetters.getVendorName(product.value)),
    breadcrumbs: computed(() =>
      productGetters.getProductCategoryBreadcrumbs(product.value, BRAND, {
        text: 'All Categories',
        link: `/${CATEGORY_ROUTE_PREFIX}`,
      }),
    ),
    productSpecifications: computed(() => ({
      contentType: 'attribute-list',
      title: 'Specifications',
      content: productGetters.getProductSpecification(product.value) ?? [],
    })),
    detailsAndCare: computed(() => ({
      contentType: 'html',
      title: 'Details & Care',
      content: productGetters.getProductVariantMetafield(product.value, 'detail_care'),
    })),
    weight: computed(() => productGetters.getWeight(product.value) ?? 0),
    productGallery: computed(() => getGalleryWithPlaceholder(product.value)),
    handlingTime: computed(() =>
      productGetters.getProductVariantMetafield(product.value, 'handlingTime'),
    ),
    hasOptions: computed(() => productGetters.hasOptions(product.value)),
    rrp: computed(() => productGetters.getRRP(product.value)),
    tags: computed(() => productGetters.getTags(product.value)),
    isDeal: computed(() => productGetters.isDeal(product.value)),
    dealExpiryDate: computed(() => productGetters.getDealExpiryDate(product.value)),
    collections: computed(() => productGetters.getCollections(product.value)),
    options: computed(() => productGetters.getOptions(product.value, true)),
    bundles: computed(() => productGetters.getBundledProductsInformation(product.value)),
    isBundle: computed(() => productGetters.isBundle(product.value)),
    variants: computed(() => productGetters.getVariants(product.value)),
    variantId: computed(() => productGetters.getVariantId(product.value)),
    hasFitment: computed(
      () => productGetters.getProductMetafield(product.value, 'hasFitment') === 'true',
    ),
    vehicleIds: computed(() => productGetters.fitmentVehicles(product.value)),
    isInStock: computed(() => productGetters.isInStock(product.value)),
    availability: computed(() => productGetters.getStockStatus(product.value)),
    isAvailableForSale: computed(() => productGetters.getSaleStatus(product.value)),
    isInStoreOnly: computed(() => productGetters.isProductInStoreOnly(product.value)),
    suitedMakes: computed(() => productGetters.getProductMetafield(product.value, 'suitedMakes')),
    suitedModels: computed(() => productGetters.getProductMetafield(product.value, 'suitedModels')),
    documentation: computed(() => productGetters.getDocumentation(product.value)),
    unNumber: computed(() => productGetters.getProductMetafield(product.value, 'unNumber')),
    hasDealExpiry: computed(() => productGetters.hasDealExpiry(product.value)),
    promotionHandle: computed(() => {
      const handle = productGetters.getProductVariantMetafield(
        product.value,
        'productPromoCollection',
      );
      if (!handle || typeof handle !== 'string') {
        return null;
      }

      return handle.replace(`${SEARCH_CATEGORY_TENANT_PREFIX_HANDLE}promotions-`, '/promotions/');
    }),
    promotionImage: computed(() => {
      return productGetters.getProductMetafield(product.value, 'productPromoImage');
    }),
    isUniversal: computed(() => productGetters.isUniversal(product.value)),
    fitmentVehicles: computed(() => productGetters.fitmentVehicles(product.value)),
    isSpecialOrder: computed(() => productGetters.isSpecialOrder(product.value)),
    isBackorder: computed(() => productGetters.isBackorder(product.value)),
    productAttributes: computed(() => {
      return {
        title: 'Product attributes',
        contentType: 'attribute-list',
        content: productGetters.getProductAttribute(product.value),
      };
    }),
    metaDescription: computed(() => productGetters.getSeoDescription(product.value)),
    requiresShipping: computed(() => productGetters.getRequiresShipping(product.value)),
    relatedProductSKUs: computed(() => {
      const relatedProducts = productGetters.getProductMetafield(product.value, 'relatedProducts');
      if (!relatedProducts) {
        return [];
      }
      return relatedProducts?.split(',')?.map((sku: string) => sku.split('#')[0]); // ABC123#1 -> ABC123
    }),
  };
};
